import { useState, useEffect, useMemo } from "react";
import { useParams, useLocation } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { AiOutlineShoppingCart, AiOutlineCar } from "react-icons/ai";

import { addToCart, cartSkusSelector } from "../../redux/cart";
import { selectMechanicId } from "../../redux/mechanic";

import { useSkuPriceTiering } from "../../hooks/useSkuPriceTiering";

import { getSkuDetails } from "../../services/sku";

import { isValidUrl } from "../../helpers/isValidUrl";

import QuantityControls from "../../components/QuantityControls/QuantityControls";
import Ripple from "../../components/ui/spinners/ripple/Ripple";
import Header from "../../components/Categories/Header/Header";
import Breadcrumb from "../../components/ui/Breadcrumb/Breadcrumb";
import SkuDetailsTable from "./SkuDetailsTable/SkuDetailsTable";
import ImageMagnifier from "../../components/ui/ImageMagnifier/ImageMagnifier";
import ImageModal from "../../components/ui/Modal/ImageModal/ImageModal";
import { PriceDiscount } from "../../components/ui";
import MechanicBasedView from "../../components/MechanicBasedView";

import { SkuPriceTiers } from "../../components/SkuDetails";
import logo from "../../assets/images/logo.png";
import SubstituteSkuList from "./substituteSkuList/SubstituteSkuList";
import RelevantSkuList from "./relevantSkuList/RelevantSkuList";

const ProductDescription = () => {
  const [productDetails, setProductDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const [crumbs, setCrumbs] = useState([]);
  const [currentSelectedImage, setCurrentSelectedImage] = useState(null);
  const [imageModalVisible, setImageModalVisible] = useState(false);

  const { productId } = useParams();
  const dispatch = useDispatch();
  const cartSkus = useSelector(cartSkusSelector);
  const selectedMechanicId = useSelector(selectMechanicId);
  const {
    isSkuTotalPriceVisible,
    skuTotalPrice,
    skuTotalPriceAfterDiscount,
    nextPriceTier,
  } = useSkuPriceTiering(
    Number(productId),
    productDetails && productDetails.priceTierList.length > 0
      ? productDetails.priceTierList[0]
      : null
  );
  const { state } = useLocation();
  const referrer = state?.from;

  useEffect(() => {
    const params = { mechanicId: selectedMechanicId };
    const getProductDetails = async () => {
      setLoading(true);
      try {
        const response = await getSkuDetails(productId, params);
        const sku = response.data.sku;
        setProductDetails(sku);
        const { categoryDetails } = sku;
        setCrumbs([
          { to: "/", name: "الأقسام" },
          {
            to:
              referrer === "/recommended-skus"
                ? "/recommended-skus"
                : `/skus?categoryName=${categoryDetails.name}&categoryId=${categoryDetails.id}`,
            name:
              referrer === "/recommended-skus"
                ? "المنتجات المختارة"
                : categoryDetails.name,
          },
          { to: `/product/${productId}`, name: sku.commercialName },
        ]);
      } catch (error) {
        console.log(error);
      }
      setLoading(false);
    };
    if (!isNaN(parseInt(productId)) && selectedMechanicId) {
      getProductDetails();
    }
  }, [productId, referrer, selectedMechanicId]);

  const skuInCart = useMemo(() => {
    return cartSkus.find((sku) => sku.id === Number(productId));
  }, [cartSkus, productId]);

  const productAddedToCartHandler = () => {
    const sku = {
      ...productDetails,
      id: Number(productId),
      availableQuantity: productDetails.quantity,
      nextPriceTier: productDetails.priceTierList[0],
    };
    sku.quantity = 1;
    dispatch(addToCart(sku));
  };

  const skuImageClickedHandler = () => {
    setImageModalVisible(true);
  };
  const imageModalClosedHandler = () => {
    setImageModalVisible(false);
  };

  useEffect(() => {
    if (productDetails) {
      setCurrentSelectedImage(
        isValidUrl(productDetails.imageList[0]?.imageUrl)
          ? productDetails.imageList[0]?.imageUrl
          : logo
      );
    }
  }, [productDetails]);

  const currentImageChangedHandler = (image) => {
    setCurrentSelectedImage(isValidUrl(image.imageUrl) ? image.imageUrl : logo);
  };

  let content;
  if (loading) {
    content = <Ripple className="border-dark-blue" />;
  } else if (productDetails) {
    content = (
      <div className="flex flex-col lg:flex-row gap-6">
        <div className="w-full lg:w-1/2 order-2 lg:order-1">
          <article
            dir="rtl"
            className="mb-4 py-6 px-4 border border-light-gray rounded-lg"
          >
            <div className="mb-7">
              <p className="text-lg font-semibold text-#677181">
                {productDetails.skuBrand}
              </p>
              <p className="text-xl font-bold">
                {productDetails.commercialName}
              </p>
              <p className="text-lg font-bold text-pink-900">ID: {productId}</p>

              {!!productDetails.skuDescription && (
                <p>
                  {" "}
                  <span className="font-bold"> الوصف :</span>{" "}
                  {productDetails.skuDescription}
                </p>
              )}
            </div>

            <MechanicBasedView>
              <div className="mt-auto flex justify-between items-center">
                <div className="mb-6 px-12 w-1/2 text-center border-l">
                  <p className="flex justify-between">
                    <span>الكمية المتاحة</span>
                    <span className="text-#216DA6 font-extrabold">
                      {productDetails.quantity || "-"}
                    </span>
                  </p>
                  <p className="flex justify-between">
                    <span>سعر المخزن</span>
                    <span className="text-#216DA6 font-extrabold">
                      {productDetails.inventoryPrice || "-"} {"جم "}
                    </span>
                  </p>
                </div>
                <div className="w-1/2 text-center">
                  <p>سعر السوق</p>
                  <span className="text-#216DA6 font-extrabold">
                    {productDetails.marketPrice || "-"} {"جم "}
                  </span>
                </div>
              </div>

              <hr />

              {isSkuTotalPriceVisible && (
                <div className="my-4 px-4 py-1 border border-slate-400 rounded-md bg-slate-100 drop-shadow-sm flex flex-wrap justify-between text-center">
                  <p className="flex-grow">إجمالي السعر الحالي :</p>
                  <PriceDiscount
                    oldPrice={skuTotalPrice}
                    newPrice={skuTotalPriceAfterDiscount}
                    className="flex-grow"
                  />
                </div>
              )}

              {productDetails.priceTierList.length > 0 && (
                <SkuPriceTiers
                  tiers={productDetails.priceTierList}
                  nextTier={nextPriceTier}
                  skuPrice={productDetails.inventoryPrice}
                  mechanicSkuPriceTieringType={
                    productDetails.skuPriceTieringType
                  }
                />
              )}

              <div className="mt-7 flex justify-center items-center">
                {skuInCart ? (
                  <QuantityControls product={skuInCart} removeOnLessOne />
                ) : (
                  <button
                    className="bg-dark-blue text-white p-4 w-full rounded-lg flex gap-2.5 justify-center items-center"
                    onClick={productAddedToCartHandler}
                  >
                    <>
                      <AiOutlineShoppingCart size={20} />
                      <span>أضف إلي العربة</span>
                    </>
                  </button>
                )}
              </div>
            </MechanicBasedView>
          </article>

          <article
            dir="rtl"
            className="border border-light-gray py-6 px-4 rounded-lg overflow-auto"
          >
            <header dir="rtl">
              <h4 className="text-start flex items-center gap-2 mb-6">
                <AiOutlineCar size={25} />
                <span>تتلائم القطعة مع :</span>
              </h4>
            </header>
            <SkuDetailsTable
              cars={productDetails.carList}
              subCategoryId={productDetails.subCategoryId}
              categoryDetails={productDetails.categoryDetails}
            />
          </article>
        </div>
        <div className="w-full lg:w-1/2 order-1 lg:order-2 flex flex-col items-center sm:flex-row sm:items-start gap-7">
          <div className="w-full sm:w-10/12 flex order-2 sm:order-1 justify-center">
            <ImageMagnifier
              imageSource={currentSelectedImage}
              imageAlt={productDetails.name}
              onImageClick={skuImageClickedHandler}
            />
            {imageModalVisible && (
              <ImageModal
                close={imageModalClosedHandler}
                imageSource={currentSelectedImage}
                imageAlt={productDetails.name}
              />
            )}
          </div>
          <div className="w-full sm:w-2/12 flex flex-row sm:flex-col order-1 sm:order-2 gap-2">
            {productDetails.imageList.map((image, index) => (
              <button
                key={index}
                className={`w-full h-20 flex justify-center items-center rounded-lg border ${
                  currentSelectedImage ===
                  (isValidUrl(image.imageUrl) ? image.imageUrl : logo)
                    ? "border-dark-blue"
                    : ""
                }`}
                onClick={() => currentImageChangedHandler(image)}
              >
                <img
                  src={isValidUrl(image.imageUrl) ? image.imageUrl : logo}
                  alt={productDetails.name}
                  className="w-10/12 h-4/5 object-center object-contain"
                />
              </button>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col min-h-screen">
      <Header hideSearch />
      <main className="my-16 mx-2 xs:mx-14">
        {crumbs.length !== 0 && (
          <Breadcrumb crumbs={crumbs} classNames="mb-8" />
        )}
        {content}
      </main>
      <SubstituteSkuList />
      <RelevantSkuList />
    </div>
  );
};

export default ProductDescription;
